/**
 * @author: lv.chen
 * @date: 2025/4/27
 * @desc: 自定义标签Select组件，多选，支持勾选预定义的标签、自定义标签
 */
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useLatest } from 'ahooks';
import {
  ChangeContext,
  Dropdown,
  DropdownProps,
  DropdownKeyDownContext,
  SelectOption,
} from 'tea-component';
import SelectButton from './components/SelectButton';
import TagCheckersPanel from './components/TagCheckersPanel';
import CustomizeTagsInputPanel from './components/CustomizeTagsInputPanel';
import useSearch from '@gp/components/Select/_hooks/useSearch';
import useCustomizeTagSelect from './_hooks/useCustomizeTagSelect';
import useSearchSelect from './_hooks/useSearchSelect';
import { defaultSelectFilter, sortByChars } from '@gp/utils/common';
import { formatOptions } from '@gp/components/Select/utils';
import { getDividedOptions } from '@gp/components/SelectMultiple/utils';
import { mergeArray, formatTagName } from './utils';
import { defaultOptionTextRenderer } from '@gp/components/Select/utils/renderers';
import { TagSelectType } from './enums';
import { ALL_OPTION, GROUPS } from '@gp/constants/select';

import type { IBaseProps } from '@home/types/common';
import type { ISelectFilter } from '@gp/types/global';
import type { ICustomizeTagSelectValue } from './types';

import './index.scss';

export interface ICustomizeTagSelectProps extends IBaseProps {
  loading?: boolean;
  ignoreCase?: boolean;
  invalidCustomizeTagChars?: string[];
  invalidCustomizeTagCharsMsg?: string;
  placeholder?: string;
  customizePlaceholder?: string;
  dropdownProps?: DropdownProps;
  deletable?: boolean | ((option: IGPSelectOption) => boolean);
  generateCustomizeAddOptionValue?: (text?: string) => string;
  generateCustomizeAddOptionData?: (value?: string, text?: string) => unknown;
  // option的text的render方法
  optionTextRenderer?: IGPSelectOptionTextRenderer;
  allOption?: false | SelectOption;
  filter?: ISelectFilter;
  options?: IGPSelectOption[] | null;
  value?: ICustomizeTagSelectValue;
  onChange?: (
    value: ICustomizeTagSelectValue,
    context: IChangeContext<React.SyntheticEvent<Element, Event>> | null,
  ) => void;
}

const CustomizeTagSelect: React.FC<ICustomizeTagSelectProps> = (props) => {
  const {
    loading,
    ignoreCase,
    invalidCustomizeTagChars = [','],
    invalidCustomizeTagCharsMsg = 'invalid , chars',
    placeholder,
    customizePlaceholder = 'Press enter to create multiple custom tags.',
    dropdownProps,
    deletable,
    generateCustomizeAddOptionValue,
    generateCustomizeAddOptionData,
    optionTextRenderer = defaultOptionTextRenderer,
    allOption = ALL_OPTION,
    groups = GROUPS,
    filter = defaultSelectFilter,
    options: optionsProps,
    value: valueProps,
    onChange: onChangeProps,
  } = props;

  const {
    className: dropdownClassName,
    overlayClassName: dropdownOverlayClassName,
    boxClassName: dropdownBoxClassName,
    appearance: dropdownAppearance = 'button',
    matchButtonWidth: dropdownMatchButtonWidth = true,
    clickClose: dropdownClickClose = false,
    updateOnChildrenChange: dropdownUpdateOnChildrenChange = true,
    preventMouseDown: dropdownPreventMouseDown = false,
    onOpen: dropdownOnOpen,
    onClose: dropdownOnClose,
    onKeyDown: dropdownOnKeyDown,
    placementOffset: dropdownPlacementOffset = 10,
    ...restDropdownProps
  } = dropdownProps || {};

  const optionTextRendererLatest = useLatest<IGPSelectOptionTextRenderer>(optionTextRenderer);

  const [open, setOpen] = useState(false);

  const [selectType, setSelectType] = useState<TagSelectType>(TagSelectType.Common);

  const [options, setOptions] = useState<IGPSelectOption[]>([]);

  const { searchMode, searchValue, onSearch } = useSearch({ searchable: true });

  const {
    dividedOptions,
    setDividedOptions,
    value,
    setValue,
    onChange: handleChange,
    onOpen: handleOpen,
    onClose: handleClose,
  } = useCustomizeTagSelect({
    loading,
    required: false,
    sortable: false,
    options,
    initialOptions: optionsProps,
    initialValue: valueProps?.checked,
    onChange: (payload, context) => {
      onChangeProps?.({ checked: payload || [], options }, context);
    },
    onChangePreProcess: (payload, context) => {
      if (searchMode) {
        onChangeWhenSearching(payload, context);
        return false;
      }
      return true;
    },
    onOpen: () => {
      setSelectType(TagSelectType.Common);
      dropdownOnOpen?.();
    },
    onClose: () => {
      dropdownOnClose?.();
    },
  });

  const {
    searchingGroups,
    searchingOptions,
    setSearchingOptions,
    searchingValue,
    setSearchingValue,
    onChangeWhenSearching,
  } = useSearchSelect({
    searchMode,
    searchValue,
    filter,
    options,
    optionTextRenderer,
    value,
    setValue,
  });

  const handleSearch = (
    payload?: string,
    context?: ChangeContext<React.SyntheticEvent<Element, Event>>,
  ) => {
    if (!payload) {
      const optionsTmp = formatOptions(options, { optionTextRenderer });
      const dividedOptionsTmp = getDividedOptions(optionsTmp, value);
      setDividedOptions(dividedOptionsTmp);
    }
    onSearch(payload, context);
  };

  const handleCustomizeAdd = (payload: IGPSelectOption[]) => {
    const payloadValues = payload?.map((item) => item?.value);

    if (searchMode) {
      const searchingValueTmp = mergeArray(payloadValues, searchingValue);
      setSearchingValue(searchingValueTmp);

      const searchingOptionsTmp = mergeArray(searchingOptions, payload);
      setSearchingOptions(searchingOptionsTmp);
    }

    const valueTmp = mergeArray(payloadValues, value);
    setValue(valueTmp);

    const optionsTmp = mergeArray(options, payload);
    const optionsSorted = sortByChars('label', optionsTmp);
    setOptions(optionsSorted);

    const dividedOptionsTmp = getDividedOptions(optionsSorted, valueTmp);
    setDividedOptions(dividedOptionsTmp);

    setSelectType(TagSelectType.Common);
  };

  const handleDelete = (payload?: string) => {
    if (!payload) return;

    if (searchMode) {
      const searchingOptionsTmp =
        searchingOptions?.filter((option) => option?.value !== payload) || [];
      setSearchingOptions(searchingOptionsTmp);
    }

    const optionsTmp = options?.filter((option) => option?.value !== payload) || [];
    setOptions(optionsTmp);

    const valueTmp = value?.filter((item) => item !== payload);
    const dividedOptionsTmp = getDividedOptions(optionsTmp, valueTmp);
    setDividedOptions(dividedOptionsTmp);

    handleUnCheckedTag(payload, optionsTmp);
  };

  const handleUnCheckedTag = (payload?: string, optionsPayload = options) => {
    if (!payload) return;

    if (searchMode) {
      const searchingValueTmp = searchingValue?.filter((item) => item !== payload);
      setSearchingValue(searchingValueTmp);
    }

    const valueTmp = value?.filter((item) => item !== payload);
    onChangeProps?.({ checked: valueTmp || [], options: optionsPayload }, null);
    setValue(valueTmp);
  };

  useEffect(() => {
    const optionsTmp = formatOptions(optionsProps, {
      optionTextRenderer: optionTextRendererLatest.current,
    });
    const optionsSorted = sortByChars('label', optionsTmp);
    setOptions(optionsSorted);
  }, [optionsProps]);

  return (
    <Dropdown
      {...restDropdownProps}
      className={classnames('dbg-customize-tag-select-dropdown', dropdownClassName)}
      overlayClassName={classnames(
        'dbg-customize-tag-select-dropdown__overlay',
        dropdownOverlayClassName,
      )}
      boxClassName={classnames('dbg-customize-tag-select-dropdown__box', dropdownBoxClassName)}
      appearance={dropdownAppearance}
      matchButtonWidth={dropdownMatchButtonWidth}
      clickClose={dropdownClickClose}
      updateOnChildrenChange={dropdownUpdateOnChildrenChange}
      preventMouseDown={dropdownPreventMouseDown}
      onOpenChange={setOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>, context: DropdownKeyDownContext) => {
        dropdownOnKeyDown?.(e, context);
        return false;
      }}
      placementOffset={dropdownPlacementOffset}
      button={
        <SelectButton
          placeholder={placeholder}
          formatter={(payload?: string) => formatTagName(options, payload)}
          tags={value}
          searchValue={searchValue}
          onSearch={handleSearch}
          onDelete={handleUnCheckedTag}
          onClick={(e) => {
            // Dropdown打开时，在这里阻止事件冒泡，防止点击SelectButton时又把Dropdown关闭了
            if (open) {
              e?.stopPropagation();
            }
          }}
        />
      }
    >
      {selectType === TagSelectType.Common && (
        <TagCheckersPanel
          searchMode={searchMode}
          deletable={deletable}
          allOption={searchMode ? false : allOption}
          groups={searchMode ? searchingGroups : groups}
          options={searchMode ? searchingOptions : dividedOptions}
          value={searchMode ? searchingValue : value}
          onChange={handleChange}
          onDelete={handleDelete}
          onClickCustomize={() => {
            setSelectType(TagSelectType.Customize);
          }}
        />
      )}
      {selectType === TagSelectType.Customize && (
        <CustomizeTagsInputPanel
          ignoreCase={ignoreCase}
          invalidCustomizeTagChars={invalidCustomizeTagChars}
          invalidCustomizeTagCharsMsg={invalidCustomizeTagCharsMsg}
          placeholder={customizePlaceholder}
          existedOptions={options}
          generateCustomizeAddOptionValue={generateCustomizeAddOptionValue}
          generateCustomizeAddOptionData={generateCustomizeAddOptionData}
          onBack={() => {
            setSelectType(TagSelectType.Common);
          }}
          onCustomizeAdd={handleCustomizeAdd}
        />
      )}
    </Dropdown>
  );
};

export default CustomizeTagSelect;
